import VenueMap, { type MapPin, type LegendItem } from './VenueMap';

/**
 * EventMaps — three maps for the WICnic:
 *   1. Overview  — wide road map, both venues, "getting there"
 *   2. Golf      — clubhouse + parking detail, verified
 *   3. Grounds   — ADCO park map (PDF), entrance + parking + pavilions
 *
 * Coordinates are percentages of each image box, so they survive resizing.
 *
 * Map images go in public/images/ (same-origin; see PROJECT_BRIEF §3 #7):
 *   map-overview.png      (wide road overview)
 *   map-golf.png          (golf clubhouse area)
 *   map-grounds-pdf.png   (ADCO illustrated park map)
 *
 * All pins (overview, golf, grounds) verified against the rendered maps.
 * If a base image is swapped, re-derive coordinates with scripts/pin-placer.html
 * (click the real spots; paste the resulting { num, x, y } lines).
 */

/* -- 1. Overview -- both venues against the major roads -- */
const overviewPins: MapPin[] = [
  { num: 1, x: 20.6, y: 23.6 }, // Riverdale Golf Courses -- verified
  { num: 2, x: 30.1, y: 61.7 }, // Riverdale Regional Park -- verified
];

const overviewLegend: LegendItem[] = [
  {
    num: 1,
    label: 'Golf — Riverdale Dunes',
    detail: 'Off Riverdale Rd · morning tournament',
  },
  {
    num: 2,
    label: 'WICnic — Riverdale Regional Park',
    detail: 'Park Blvd & Henderson Rd · afternoon',
  },
];

/* -- 2. Golf detail -- verified -- */
const golfPins: MapPin[] = [
  { num: 1, x: 42, y: 38 },  // Golf parking (left) -- NW of clubhouse
  { num: 2, x: 51, y: 50 },  // Clubhouse / Riverdale Restaurant (right)
];

const golfLegend: LegendItem[] = [
  { num: 1, label: 'Golf parking', detail: 'Northwest of the clubhouse' },
  {
    num: 2,
    label: 'Clubhouse — check-in & breakfast',
    detail: 'Riverdale Restaurant · 7:30 AM · shotgun start 8:30',
  },
];

/* -- 3. Grounds detail -- ADCO PDF base -- */
// Coords are % of the grounds image (960x622). Verified via pin-placer.html.
const groundsPins: MapPin[] = [
  { num: 1, x: 41.1, y: 83.4 }, // Park entrance / traffic circle
  { num: 2, x: 38.1, y: 48.6 }, // Parking lot 1 — west side, north of entrance
  { num: 2, x: 47.4, y: 23.3 }, // Parking lot 2 — west side, up the spine
  { num: 3, x: 59, y: 20.9 },   // WICnic pavilions / BBQ
];

const groundsLegend: LegendItem[] = [
  {
    num: 1,
    label: 'Park entrance',
    detail: 'Park Blvd & Henderson Rd traffic circle, then north',
  },
  { num: 2, label: 'Parking', detail: 'Two lots, west side of Park Blvd' },
  {
    num: 3,
    label: 'WICnic area & BBQ',
    detail: 'Pavilions off Campground Rd · kickoff 1:00 PM · BBQ 2:30 PM',
  },
];

export default function EventMaps() {
  return (
    <div className="event-maps">
      <VenueMap
        eyebrow="Getting there"
        title="The park & the course"
        imageSrc="/images/map-overview.png"
        imageAlt="Overview map showing the golf course and Riverdale Regional Park relative to E-470 and the major roads"
        pins={overviewPins}
        legend={overviewLegend}
      />
      <VenueMap
        eyebrow="Morning · Golf"
        title="Riverdale Dunes"
        imageSrc="/images/map-golf.png"
        imageAlt="Golf venue map showing the clubhouse and parking"
        pins={golfPins}
        legend={golfLegend}
      />
      <VenueMap
        eyebrow="Afternoon · WICnic"
        title="Riverdale Regional Park"
        imageSrc="/images/map-grounds-pdf.png"
        imageAlt="WICnic grounds map showing the entrance, parking, and pavilions"
        pins={groundsPins}
        legend={groundsLegend}
      />
    </div>
  );
}
